import { readFileSync } from 'fs';
import { fileURLToPath } from 'url';
import { dirname, join } from 'path';
import { CloudGridError, setVerbose, isVerbose } from './utils.js';
import { PollTimeoutError } from './services/plug-poll.js';
import { friendlyNetworkMessage } from './api/fetch-errors.js';
import { emitTerminalCloudGridError } from './handle-error.js';
import { checkForUpdates } from './update-check.js';
import { enforceVersionPolicy } from './version-policy.js';
import { buildProgram } from './program.js';
import { formatVersion, getVersionInfo } from './version.js';

const __dirname = dirname(fileURLToPath(import.meta.url));
const pkg = JSON.parse(readFileSync(join(__dirname, '../package.json'), 'utf-8'));

// Decision 016 §"Privacy and anonymization" — `--version --verbose` is one
// of three discoverability surfaces for telemetry status. Pre-empt
// commander's auto-handler so we can emit the verbose telemetry block.
const argvHasVersion = process.argv.includes('--version') || process.argv.includes('-V');
if (argvHasVersion) {
  const verboseFlag = process.argv.includes('--verbose') || process.argv.includes('-v');
  process.stdout.write(formatVersion(getVersionInfo(pkg.version), verboseFlag));
  process.exit(0);
}

const program = buildProgram();

if (process.argv.includes('--verbose') || process.argv.includes('-v')) {
  setVerbose(true);
}

// Backlog 200 — server-driven version policy. Runs BEFORE the friendly
// `checkForUpdates` nudge so a `block` policy short-circuits the npm-registry
// poll for users who can't continue anyway. `CLOUDGRID_SKIP_VERSION_CHECK=1`
// bypasses entirely (documented in CLOUDGRID.md + the block message).
await enforceVersionPolicy(pkg.version).catch(() => {});

await checkForUpdates(pkg.version).catch(() => {});

try {
  await program.parseAsync();
} catch (e) {
  // Backlog 544 — a poll timeout where the deploy is still progressing
  // server-side (build succeeded, or still building) is NOT a hard failure.
  // Print the softened, self-contained guidance and exit 2 (warning: didn't
  // fully verify, but nothing is broken). A genuine hard timeout still
  // exits 1 via the message + branch below.
  if (e instanceof PollTimeoutError) {
    console.error(e.softTimeout ? e.message : `Error: ${e.message}`);
    process.exit(e.softTimeout ? 2 : 1);
  }
  if (e instanceof CloudGridError) {
    console.error(`Error: ${e.message}`);
    // BL593 — a terminal CloudGridError (queued/in-process plug failure, and
    // every other verb's terminal CloudGridError) reaches here and exits
    // without ever routing through handleV2Error, so it emitted no telemetry.
    // Fire a cli_events `error` row before exiting. Best-effort, 250ms-capped,
    // swallows its own errors; awaited so the POST has a chance to complete
    // before process.exit. The printed message above is unchanged.
    await emitTerminalCloudGridError(e);
    process.exit(1);
  }
  // `grid plug` rethrows raw errors here rather than through
  // handleV2Error, so a network failure during the deploy poll lands in this
  // branch. Give it the same blameless line the other verbs get.
  const networkMsg = friendlyNetworkMessage(e);
  if (networkMsg) {
    console.error(`Error: ${networkMsg}`);
    process.exit(1);
  }
  if (isVerbose()) {
    console.error(e);
  } else if (e instanceof Error) {
    console.error(`Error: ${e.message}`);
  }
  process.exit(1);
}
